import { Panel as _Panel } from '#~/common/editor/index.js';
import { useProducer, useStore } from '../store/hooks.js';
import { Content } from './content.jsx';
import { Footer } from './footer.jsx';
import type { HtmlAttributes } from '@studio/design-system/props';
import type { Settings } from '@studio/models/control';
import { toUri } from '@studio/models/control';
import { Protocol as C_Protocol } from '@studio/models/control';
import { isRtsp } from '@studio/models/video/rtsp';
// The template->control resolution (incl. ONVIF->VISCA/IP) lives in normalize.ts so
// the first-run funnel and multi-add share it. The model-less/ONVIF RTSP defaults
// below layer on top of it, firing only when no template resolves.
import { resolveControlProtocol, resolveControlSettings } from './normalize.js';
import { useMount } from '@studio/react-utils/hooks';
import { isNotNil } from '@tryiris-ai/js-types';
import type { T_Nullable } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';

/** @internal */
const Inner = (props: HtmlAttributes): ReactElement => {
  const producer = useProducer();

  // Can we continue to the next step in the workflow?
  const isReady = useStore((µ) => {
    const { control, modelId } = µ.candidate;

    // If we are not configuring controls, carry on.
    if (!control?.protocol) {
      return true;
    }

    // A control protocol is set: readiness reduces to "the config is URI-able".
    // toUri substitutes DefaultModelId when modelId is absent (@studio/models/
    // control utils.ts) — the same generic model the commit path sends for a
    // model-less device — so this predicate mirrors commit semantics rather than
    // hard-requiring a brand/model. Gating on modelId here dead-ended
    // "Save and Finish" for a model-less RTSP add (ENG-4159 non-blocking add).
    return isNotNil(toUri(control, { modelId }));
  });

  useEffect(() => {
    producer(({ control }) => {
      if (control.isReady !== isReady) {
        control.isReady = isReady;
      }
    });
  }, [isReady]);

  return (
    <_Panel {...props}>
      <Content />
      <Footer />
    </_Panel>
  );
};

/** Control `<Panel>` component. */
export const Panel = (props: HtmlAttributes): T_Nullable<ReactElement> => {
  const producer = useProducer();
  const [isPrimed, setPrimed] = useState(false);

  // On mount, normalize the control settings.
  useMount(() => {
    queueMicrotask(() => {
      producer((draft) => {
        const {
          candidate,
          manufacturer: { config }
        } = draft;

        const { control } = candidate;

        // Same rule as before (ONVIF templates configure VISCA/IP, because the
        // app does not speak ONVIF for control) — moved to normalize.ts so the
        // first-run funnel can resolve the SAME endpoint without rendering this
        // panel. See that file's header.
        const protocol = resolveControlProtocol(control, config);

        // A resolved template with a control protocol wins, and normalize.ts builds
        // the endpoint. A protocol change starts clean (host from the video, port and
        // transport from the template), so an ONVIF control's port and login never
        // reach VISCA/IP or HTTP; a control already on that protocol keeps its
        // settings. The two fallbacks below cover a model-less or ONVIF candidate
        // when no template resolved.
        if (protocol !== undefined && config?.control?.protocol) {
          candidate.control = resolveControlSettings(control, config, candidate.video) as Settings;
        } else if (!config && !control?.protocol && isRtsp(candidate.video?.protocol)) {
          // No registry template resolved (a model-less add, or Brand="Other")
          // for an RTSP source: default the control to VISCA/IP so this step is
          // configurable instead of a blank dead end. Mirrors Multi-Add's
          // model-less default — a known model whose template omits a control
          // contract stays video-only (registry is authoritative), which is why
          // this is gated on !config, not merely !suggested. Set ONLY the
          // protocol; visca-ip.tsx's own mount normalization fills host (from
          // video), port 52381, transport UDP. The !control?.protocol guard
          // never clobbers a discovery-seeded config. (ENG-4159 non-blocking add.)
          candidate.control = { ...control, protocol: C_Protocol.VISCA_IP };
        } else if (
          !config &&
          control?.protocol === C_Protocol.ONVIF &&
          isRtsp(candidate.video?.protocol)
        ) {
          // ONVIF single-add parity (ENG-4159 follow-up). A discovered
          // RTSP-over-ONVIF camera with no resolved template reaches this step
          // with control.protocol === ONVIF: source/panel.tsx's legacy sync
          // mirrors the discovery's ONVIF control aspect verbatim into the
          // candidate, and discovery.tsx's ONVIF capabilities substitution
          // rewrites only candidate.video (to the discovered RTSP URI), never
          // the control plane. ONVIF is not a control plane we drive, so the
          // step renders it, the bridge reports "Not responding", and the camera
          // commits with a dead control. Default it to VISCA/IP so single-add
          // matches BOTH the manual model-less prefill above AND the Multi-Add /
          // VC-A61P post-probe rewrite (wiring.ts): "ONVIF is only useful as a
          // capabilities probe; the PTZ plane is VISCA." Set a CLEAN VISCA
          // control (NOT ...control): the ONVIF config carries an ONVIF port
          // (8080) and possibly credentials, and spreading them would pin the
          // wrong port and leak creds onto the VISCA/IP URX — the exact leak
          // wiring.ts avoids by building fresh ViscaIpDefaults. visca-ip.tsx's
          // mount normalization then fills host (from video), port 52381 and
          // transport UDP, identical to the manual case. Gated on !config so a
          // resolved template still wins (the suggested branch); scoped to ONVIF
          // so a genuinely user/discovery-seeded NON-ONVIF control is untouched.
          candidate.control = { protocol: C_Protocol.VISCA_IP };
        }
      });

      setPrimed(true);
    });
  });

  return isPrimed ? <Inner {...props} /> : null;
};
